import { useTranslation } from 'react-i18next'
import { Button, FormInput } from '@microservices/wiskey-react-components'
import { Check, Delete, Edit } from '@mui/icons-material'
import CloseIcon from '@mui/icons-material/Close'
import InfoIcon from '@mui/icons-material/Info'
import { Divider, Grid, Tooltip } from '@mui/material'

import { Label } from '@components/Label'
import { useVariable } from '@components/Variables/hooks'
import { VariableValue } from '@components/Variables/types'

import { getReservedVariableValue, isReservedVariable } from '@helpers'
import { GENERATOR_INPUT_TYPE, VALUE_TYPE_OPTIONS, VARIABLE_TYPE_OPTIONS } from '@constants'

type VariableProps = {
  index?: number
  removeVariable?: (index: number) => void
  variables: VariableValue[]
  initialVariables: VariableValue[]
}

const commonInputStyles = {
  width: '100%',
  maxWidth: 200,
  '& .MuiFormLabel-root': {
    transformOrigin: 'top left',
    left: 0,
    position: 'relative',
    width: '100%',
    textAlign: 'left',
    marginBottom: '4px',
    justifyContent: 'flex-start',
    display: 'flex',
  },
  '& .MuiInputBase-root': {
    height: 36,
    justifyContent: 'flex-start',
    textAlign: 'left',
  },
}

const reservedIconStyles = {
  color: 'primary.main',
  cursor: 'help',
  position: 'absolute',
  left: '-1.5rem',
  '&:hover': {
    color: 'primary.dark',
    transform: 'scale(1.15)',
  },
  transition: 'color 0.2s ease-in-out, transform 0.2s ease-in-out',
}

export const Variable = ({
  removeVariable,
  index = 0,
  variables,
  initialVariables,
}: VariableProps) => {
  const { t } = useTranslation()
  const { handlers, data } = useVariable({ index, variables, removeVariable, initialVariables })

  const {
    internalIdInputName,
    titleInputName,
    valueTypeInputName,
    variableTypeInputName,
    watchValueType,
    watchVariableType,
    watchTitle,
    watchInternalId,
    isEditVariable,
    isFormValid,
    isCreateOrEdit,
    isCreateOrEditVariable,
    isSavingVariableValues,
  } = data

  const {
    handleEditVariable,
    handleResetFields,
    handleSave,
    handleDelete,
    validateTitleAndIdVariable,
  } = handlers

  const isReserved = isReservedVariable(watchInternalId)
  const reservedVariableValue = getReservedVariableValue(watchInternalId)

  return (
    <>
      <Grid container alignItems='center' spacing={0.5} sx={{ mb: 0.5 }}>
        <Grid
          item
          sx={{ display: 'flex', alignItems: 'center', position: 'relative', pl: 2.5 }}
          xs={2.6}
        >
          {isReserved && (
            <Tooltip
              arrow
              placement='top'
              title={t('configEntity.variables.reservedVariable.tooltip', {
                value: reservedVariableValue || '',
              })}
            >
              <InfoIcon sx={reservedIconStyles} />
            </Tooltip>
          )}
          {isEditVariable && (
            <FormInput
              disabled={isCreateOrEdit}
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={`${t('configEntity.variables.internalId.label')}`}
              labelPlacement={'top'}
              name={internalIdInputName}
              sx={commonInputStyles}
              rules={{
                required: true,
                validate: validateTitleAndIdVariable,
              }}
            />
          )}
          {!isEditVariable && (
            <Label justifyContent='flex-start' sx={{ height: '100%' }}>
              {watchInternalId}
            </Label>
          )}
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center' }} xs={2.6}>
          {isEditVariable && (
            <FormInput
              inputType={GENERATOR_INPUT_TYPE.INPUT}
              label={`${t('configEntity.variables.variableTitle.label')}`}
              labelPlacement={'top'}
              name={titleInputName}
              sx={commonInputStyles}
              rules={{
                required: true,
                validate: validateTitleAndIdVariable,
              }}
            />
          )}
          {!isEditVariable && (
            <Label justifyContent='flex-start' sx={{ height: '100%' }}>
              {watchTitle}
            </Label>
          )}
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center' }} xs={2.6}>
          {isEditVariable && (
            <FormInput
              autocompleteOptions={VARIABLE_TYPE_OPTIONS}
              inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
              label={`${t('configEntity.variables.variableType.label')}`}
              labelPlacement={'top'}
              name={variableTypeInputName}
              sx={commonInputStyles}
              rules={{
                required: true,
              }}
            />
          )}
          {!isEditVariable && (
            <Label justifyContent='flex-start' sx={{ height: '100%' }}>
              {watchVariableType?.label}
            </Label>
          )}
        </Grid>
        <Grid item sx={{ display: 'flex', alignItems: 'center' }} xs={2.6}>
          {isEditVariable && (
            <FormInput
              autocompleteOptions={VALUE_TYPE_OPTIONS}
              inputType={GENERATOR_INPUT_TYPE.AUTOCOMPLETE}
              label={`${t('configEntity.variables.valueType.label')}`}
              labelPlacement={'top'}
              name={valueTypeInputName}
              sx={commonInputStyles}
              rules={{
                required: true,
              }}
            />
          )}
          {!isEditVariable && (
            <Label justifyContent='flex-start' sx={{ height: '100%' }}>
              {watchValueType?.label}
            </Label>
          )}
        </Grid>

        <Grid item textAlign='center' xs={1}>
          {isEditVariable ? (
            <Grid container justifyContent='center' pt={2} spacing={0.2}>
              <Grid item>
                <Button disabled={!isFormValid || isSavingVariableValues} onClick={handleSave}>
                  <Check
                    color={isFormValid && !isSavingVariableValues ? 'action' : 'disabled'}
                    fontSize='small'
                  />
                </Button>
              </Grid>
              <Grid item>
                <Button disabled={isSavingVariableValues} onClick={handleResetFields}>
                  <CloseIcon color={'error'} fontSize='small' />
                </Button>
              </Grid>
            </Grid>
          ) : (
            <Grid container justifyContent='center' spacing={0.2}>
              <Grid item>
                <Button
                  disabled={isCreateOrEdit || isEditVariable || isCreateOrEditVariable}
                  onClick={() => handleEditVariable(true)}
                >
                  <Edit
                    color={isCreateOrEdit || isCreateOrEditVariable ? 'disabled' : 'action'}
                    fontSize='small'
                  />
                </Button>
              </Grid>
              <Grid item>
                <Button
                  disabled={isCreateOrEdit || isEditVariable || isCreateOrEditVariable}
                  onClick={handleDelete}
                >
                  <Delete
                    color={isCreateOrEdit || isCreateOrEditVariable ? 'disabled' : 'action'}
                    fontSize='small'
                  />
                </Button>
              </Grid>
            </Grid>
          )}
        </Grid>
        <Divider sx={{ width: '95%', my: 0.5 }} />
      </Grid>
    </>
  )
}
